import { useState } from "react";
import GameSelector from "@/components/games/GameSelector";
import P5GameContainer from "@/components/games/P5GameContainer";
import { GameGeneratorDialog } from "@/components/games/GameGeneratorDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { gameService } from "@/services/gameService";
import { useToast } from "@/hooks/use-toast";
import type { Game, GameSession } from "@/types/games";
import { useChatActions } from "../ChatActionContext";

type Spec = { grade: string; subject?: string | null };

export function InlineGames({ spec }: { spec: Spec }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { reportActivity } = useChatActions();
  const [game, setGame] = useState<Game | null>(null);
  const [session, setSession] = useState<GameSession | null>(null);
  const [loading, setLoading] = useState(false);

  const start = async (g: Game) => {
    setLoading(true);
    try {
      const s: GameSession = user
        ? await gameService.createGameSession(g.id, spec.grade, g.game_type, user.id)
        : {
            id: `guest-${crypto.randomUUID()}`,
            user_id: "guest",
            game_id: g.id,
            grade: spec.grade,
            subject: g.game_type,
            difficulty_level: 1,
            session_data: {},
            score: 0,
            max_score: 0,
            time_spent: 0,
            problems_attempted: 0,
            problems_correct: 0,
            hints_used: 0,
            completed: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
      setGame(g);
      setSession(s);
    } catch (e: any) {
      toast({ title: "Couldn't start game", description: e?.message ?? "", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const finish = async (completed: GameSession) => {
    if (game) {
      reportActivity(
        `The user just finished the game "${game.name}" (grade ${spec.grade}): score ${completed.score}, ${completed.problems_correct ?? "?"}/${completed.problems_attempted ?? "?"} problems correct.${user ? "" : " (Guest — not saved.)"}`,
      );
    }
    if (user && !completed.id.startsWith("guest-")) {
      try {
        await gameService.updateGameSession(completed.id, {
          score: completed.score,
          time_spent: completed.time_spent,
          completed: true,
        });
      } catch { /* ignore */ }
    }
    toast({ title: "Game complete", description: `Score: ${completed.score}` });
    setGame(null);
    setSession(null);
  };

  if (game && session) {
    return (
      <Card className="overflow-hidden">
        <CardContent className="p-2">
          <P5GameContainer
            game={game}
            gameSession={session}
            onComplete={finish}
            onExit={() => { setGame(null); setSession(null); }}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm font-medium">
            Games · Grade {spec.grade}{spec.subject ? ` · ${spec.subject}` : ""}
          </div>
          <GameGeneratorDialog grade={spec.grade} onGameReady={start} />
        </div>
        {loading ? (
          <div className="text-sm text-muted-foreground">Starting game…</div>
        ) : (
          <GameSelector grade={spec.grade} subject={spec.subject ?? undefined} onGameSelect={start} />
        )}
      </CardContent>
    </Card>
  );
}
